import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import { resolveImageUrl } from '@/utils/imageUrl';
import axios from 'axios';
import {
  Heart, MessageCircle, Bookmark, MoreHorizontal, GraduationCap,
  TrendingUp, Image, Loader2, ArrowRight, Sparkles, X, ChevronLeft,
  ChevronRight, Pencil, Trash2, Flag, MapPin, Send, Repeat2,
  Home, BookOpen, Briefcase, Utensils, Plane, Lightbulb, CheckCircle2, Shield, Lock,
  Search, Star
} from 'lucide-react';
import { ShareMenu } from '@/components/ShareMenu';
import { CommentSection } from '@/components/CommentSection';
import { ClaimOfferModal } from '@/components/ClaimOfferModal';
import BookConsultationModal from '@/components/BookConsultationModal';
import { useActionStates } from '@/hooks/useActionStates';
import { useSmartApply } from '@/lib/smartApply';
import { useGuestGate } from '@/hooks/useGuestGate';
import GuestGateModal from '@/components/GuestGateModal';
import { useApplyContext } from '@/lib/smartApply';
import TrustBadge from '@/components/TrustBadge';
import LiveTrackingCard from '@/components/LiveTrackingCard';
import { AdCarousel, InFeedAd } from '@/components/FeedAds';
import { useFeedIntelligence, FeedIntelCard } from '@/components/FeedIntelligence';
import FlowLiveCard from '@/components/FlowLiveCard';
import AgentComposeModal from '@/components/AgentComposeModal';
import { toast } from 'sonner';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { usePlatformFlags } from '@/hooks/usePlatformFlags';
import { SEOHead } from '@/components/SEOHead';
import DesktopLayout from '@/components/DesktopLayout';

const MOCK_STORIES = [
  { id: 1, name: 'Your Story', img: 'https://images.unsplash.com/photo-1607332646791-929f9ddcf96a?w=100&h=100&fit=crop', isOwn: true },
  { id: 2, name: 'MIT', img: 'https://images.unsplash.com/photo-1632988663082-4bac2c1847a0?w=100&h=100&fit=crop', isUni: true },
  { id: 3, name: 'Sarah K', img: 'https://images.unsplash.com/photo-1587923266174-990d6b704990?w=100&h=100&fit=crop' },
  { id: 4, name: 'Oxford', img: 'https://images.unsplash.com/photo-1640342538716-286e4e27e104?w=100&h=100&fit=crop', isUni: true },
  { id: 5, name: 'Alex M', img: 'https://images.unsplash.com/photo-1589323287989-45c163172c23?w=100&h=100&fit=crop' },
  { id: 6, name: 'Stanford', img: 'https://images.unsplash.com/photo-1659993445173-279e4f37c216?w=100&h=100&fit=crop', isUni: true },
];

// Skeleton loader
function PostSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden animate-pulse" data-testid="post-skeleton">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-10 h-10 rounded-full bg-slate-200" />
        <div className="flex-1">
          <div className="h-3 bg-slate-200 rounded w-24 mb-1.5" />
          <div className="h-2 bg-slate-100 rounded w-16" />
        </div>
      </div>
      <div className="px-4 pb-3 space-y-2">
        <div className="h-3 bg-slate-100 rounded w-full" />
        <div className="h-3 bg-slate-100 rounded w-3/4" />
      </div>
      <div className="w-full h-48 bg-slate-100" />
      <div className="flex gap-5 px-4 py-3">
        <div className="h-5 w-12 bg-slate-100 rounded" />
        <div className="h-5 w-12 bg-slate-100 rounded" />
        <div className="h-5 w-12 bg-slate-100 rounded" />
      </div>
    </div>
  );
}

// Image Lightbox
function ImageLightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex || 0);
  if (!images?.length) return null;
  return (
    <div className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="image-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white z-10"><X className="h-7 w-7" /></button>
      {images.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 z-10"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={(e) => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 z-10"><ChevronRight className="h-5 w-5" /></button>
        </>
      )}
      <img src={images[idx]} alt="" className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white scale-125' : 'bg-white/40'}`} />)}
        </div>
      )}
    </div>
  );
}

// Report Modal
function ReportModal({ postId, onClose }) {
  const [reason, setReason] = useState('inappropriate');
  const [details, setDetails] = useState('');
  const [sending, setSending] = useState(false);
  const reasons = ['inappropriate', 'spam', 'harassment', 'misinformation', 'other'];
  const handleSubmit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/posts/${postId}/report`, { reason, details });
      toast.success('Post reported. We\'ll review it shortly.');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to report');
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-sm p-5 animate-slide-up" onClick={e => e.stopPropagation()} data-testid="report-modal">
        <h3 className="text-sm font-bold text-[#1A1A1A] mb-3">Report Post</h3>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {reasons.map(r => (
            <button key={r} onClick={() => setReason(r)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all capitalize ${reason === r ? 'bg-red-50 text-red-700 border-red-300' : 'bg-slate-50 text-slate-500 border-slate-200'}`} data-testid={`report-reason-${r}`}>
              {r}
            </button>
          ))}
        </div>
        <textarea value={details} onChange={e => setDetails(e.target.value)} placeholder="Additional details (optional)..." className="w-full p-2.5 rounded-lg border border-slate-200 text-xs resize-none h-16 outline-none focus:ring-2 focus:ring-red-100" data-testid="report-details" />
        <div className="flex gap-2 mt-3">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-100 text-slate-600 text-xs font-semibold">Cancel</button>
          <button onClick={handleSubmit} disabled={sending} className="flex-1 py-2 rounded-lg bg-red-600 text-white text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1" data-testid="submit-report-btn">
            {sending && <Loader2 className="h-3 w-3 animate-spin" />} Report
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Post Modal
function EditModal({ post, onClose, onSave }) {
  const [content, setContent] = useState(post.content);
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/posts/${post.post_id}`, { content });
      toast.success('Post updated');
      onSave(content);
      onClose();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 animate-slide-up" onClick={e => e.stopPropagation()} data-testid="edit-post-modal">
        <h3 className="text-sm font-bold text-[#1A1A1A] mb-3">Edit Post</h3>
        <textarea value={content} onChange={e => setContent(e.target.value)} className="w-full p-3 rounded-lg border border-slate-200 text-sm resize-none h-28 outline-none focus:ring-2 focus:ring-indigo-100" data-testid="edit-post-input" />
        <div className="flex gap-2 mt-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-semibold">Cancel</button>
          <button onClick={handleSave} disabled={saving || !content.trim()} className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1" data-testid="save-edit-btn">
            {saving && <Loader2 className="h-3 w-3 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

const CATEGORY_ICONS = {
  // New intent-based taxonomy
  share: Repeat2,
  ask: MessageCircle,
  looking_for: Search,
  review: Star,
  tips: Lightbulb,
  visa_experience: Plane,
  admissions: GraduationCap,
  // Legacy categories — keep so old posts still render correctly
  campus_life: GraduationCap,
  course_review: BookOpen,
  accommodation: Home,
  jobs: Briefcase,
  food_culture: Utensils,
};

const CATEGORY_LABELS = {
  share: 'Share',
  ask: 'Ask',
  looking_for: 'Looking for',
  review: 'Review',
  tips: 'Tips',
  visa_experience: 'Visa',
  admissions: 'Admissions',
  // Legacy
  campus_life: 'Campus Life',
  course_review: 'Course Review',
  accommodation: 'Accommodation',
  jobs: 'Part-time Jobs',
  food_culture: 'Food & Culture',
};

const CATEGORY_COLORS = {
  share: 'text-blue-600 bg-blue-50',
  ask: 'text-emerald-600 bg-emerald-50',
  looking_for: 'text-purple-600 bg-purple-50',
  review: 'text-amber-600 bg-amber-50',
  tips: 'text-yellow-600 bg-yellow-50',
  visa_experience: 'text-teal-600 bg-teal-50',
  admissions: 'text-indigo-600 bg-indigo-50',
  // Legacy
  campus_life: 'text-blue-600 bg-blue-50',
  course_review: 'text-purple-600 bg-purple-50',
  accommodation: 'text-green-600 bg-green-50',
  jobs: 'text-amber-600 bg-amber-50',
  food_culture: 'text-red-600 bg-red-50',
};

/* ============================================================================
 * Flow filter buckets — student-facing pills. Different from post-creation
 * categories above; these are server-side preset queries sent as `&bucket=`.
 * Each bucket maps server-side (server.py:get_posts) to a Mongo $or clause.
 *
 * Active-pill palette:
 *   - all          → navy filled (#0F2240)
 *   - scholarship  → amber filled (#F5A623) with navy text
 *   - everything   → teal filled (#1A7A6E)
 * ========================================================================== */
const FLOW_FILTERS = [
  { id: 'all',         label: 'All',          hero: false, scholar: false },
  { id: 'fee_offers',  label: 'Fee Offers',   hero: false, scholar: false,
    empty: 'No fee offers right now. Check back soon — institutions post new offers regularly.' },
  { id: 'scholarship', label: 'Scholarship',  hero: false, scholar: true,
    empty: 'No scholarships posted yet. Use the scholarship button on any course page to request one directly from an institution.' },
  { id: 'agent_offers', label: 'Agent Offers', hero: false, scholar: false,
    empty: 'No agent offers right now. Browse verified MARA agents and request a free consultation directly from their profile.',
    emptyCta: { label: 'Browse Agents →', to: '/agents' } },
  { id: 'news',         label: 'News',         hero: false, scholar: false,
    empty: 'No news updates right now. Check back soon for the latest immigration and visa news.' },
  { id: 'blogs',        label: 'Blogs',        hero: false, scholar: false,
    empty: 'No blog guides yet. Check back soon for the latest study-abroad guides and tips.' },
  { id: 'qa',           label: 'Q&A',          hero: false, scholar: false,
    empty: 'No Q&A yet. Ask a question in Flow and Unexa AI will answer instantly.' },
  { id: 'events',       label: 'Events',       hero: false, scholar: false,
    empty: 'No events posted yet. Follow institutions to get notified when they post events.' },
];

export default function FeedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isAllowed, loaded: featuresLoaded } = useFeatureFlags();
  const { isEnabled, loaded: flagsLoaded } = usePlatformFlags();

  // Redirect to /colleges if feed is disabled by platform flags OR social feed flag
  useEffect(() => {
    if (flagsLoaded && (!isEnabled('feed') || !isAllowed('social_feed'))) {
      navigate('/colleges', { replace: true });
    }
  }, [flagsLoaded, isEnabled, isAllowed, navigate]);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasApplication, setHasApplication] = useState(null);
  const [showJourneyPopup, setShowJourneyPopup] = useState(false);
  // Agent compose modal — opens when an agent taps the composer bar.
  // Students/institutions still navigate to /create (CreatePostPage).
  const [showAgentCompose, setShowAgentCompose] = useState(false);

  // Auto-open agent composer when ?compose=agent is present (e.g. agents
  // bounced here from /create, or deep-links from other pages).
  useEffect(() => {
    if (user?.role !== 'agent') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('compose') === 'agent') {
      setShowAgentCompose(true);
      // Strip the query param so refresh doesn't re-open the modal.
      const url = new URL(window.location.href);
      url.searchParams.delete('compose');
      window.history.replaceState({}, '', url.toString());
    }
  }, [user]);
  const [activeCategory, setActiveCategory] = useState('');
  // Flow filter bucket — sent server-side as `&bucket=` to map to a preset
  // Mongo query. Active "all" means no bucket param sent. Independent of
  // `activeCategory` which is still used for legacy direct-category links.
  const [activeBucket, setActiveBucket] = useState('all');
  const activeBucketRef = useRef('all');
  const [activeHashtag, setActiveHashtag] = useState('');
  const [trendingTags, setTrendingTags] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  // Flow stream search — filters intelItems (news/blog) AND posts by title,
  // body, tags, country. Empty string = no filter. Wired Mar 1, 2026.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  // "Find leads" in-place filter — when true, feed shows ONLY student lead
  // posts (looking_for / visa_experience / admissions) and hides intel +
  // sponsored cards. Toggles on/off via the "Find leads" CTA next to the
  // search bar. Stacks with searchQuery (both can be active simultaneously).
  const [findLeadsActive, setFindLeadsActive] = useState(false);
  const applyCtx = useApplyContext();
  // Personalised mix of news / blogs / Q&A injected between user posts
  const intelItems = useFeedIntelligence();

  // ─── Mirror-to-intel adapter (Feb 2026) ─────────────────────────────
  // The Flow mixer surfaces system-mirrored news/blog posts inline with
  // user posts. They carry mirror_* fields populated at publish time.
  // This helper detects them and reshapes back to the FeedIntelCard item
  // contract so they render as rich NewsCard/BlogCard instead of plain
  // PostCard markdown. Returns null when the post is a regular user post.
  const mirrorToIntel = (post) => {
    if (!post) return null;
    const src = post.source;
    if (src !== 'news_auto_publish' && src !== 'blog_auto_publish') return null;
    const type = post.mirror_content_type || (src === 'news_auto_publish' ? 'news' : 'blog');
    return {
      type,
      article_id: post.mirrored_from_article_id || post.post_id,
      title: post.mirror_title || post.content?.split('\n')[0] || '',
      summary: post.mirror_summary || '',
      cover_image: post.mirror_cover_image || '',
      read_time: post.mirror_read_time || (type === 'news' ? 2 : 5),
      slug: post.mirror_slug || '',
      author_name: post.mirror_author || 'Unexa Team',
      category: post.mirror_category || '',
      created_at: post.created_at,
    };
  };
  // Refs to avoid re-creating the IntersectionObserver on every page change,
  // which was causing the observer to immediately re-fire on the current
  // sentinel position and cascade-load every page in one shot.
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const observerRef = useRef(null);
  const activeCategoryRef = useRef('');
  const activeHashtagRef = useRef('');

  const fetchPosts = useCallback(async (pageNum = 1, append = false, catFilter = '', hashFilter = '') => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    loadingMoreRef.current = true;
    try {
      let url = `${API}/posts?page=${pageNum}&limit=10`;
      if (catFilter) url += `&category=${catFilter}`;
      if (hashFilter) url += `&hashtag=${hashFilter}`;
      if (activeBucketRef.current && activeBucketRef.current !== 'all') {
        url += `&bucket=${activeBucketRef.current}`;
      }
      const res = await axios.get(url);
      const data = res.data;
      if (append) {
        setPosts(prev => [...prev, ...(data.posts || [])]);
      } else {
        // Resilience (Feb 2026): if the backend says the feed is non-empty
        // (total > 0) but returned zero items for page 1, treat the
        // response as degraded (Atlas blip / 520 retry) and KEEP the
        // currently displayed posts. Without this, a single Cloudflare
        // 520 → empty parse → "No posts yet" splash for the user even
        // though there are 341 posts in Atlas.
        const incoming = data.posts || [];
        setPosts(prev => {
          if (incoming.length === 0 && prev.length > 0 && (data.total ?? 0) > 0) {
            // Degraded payload — keep what the user is already viewing.
            return prev;
          }
          return incoming;
        });
      }
      setHasMore(data.has_more);
      hasMoreRef.current = !!data.has_more;
      setPage(pageNum);
      pageRef.current = pageNum;
    } catch (e) {
      console.error(e);
      // Resilience (Feb 2026): on network error / 520, do NOT clear the
      // posts list — keep showing the previously-loaded feed. The user
      // never sees a blank screen on a transient prod hiccup.
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, []);

  // Callback ref — fires the moment the sentinel <div> is attached to the
  // DOM (and again with null on unmount). This guarantees we have the node
  // before creating the observer, and guarantees the observer is torn down
  // when the sentinel is removed (e.g. on filter change before next render).
  const sentinelRef = useCallback(node => {
    // Tear down any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMoreRef.current && !loadingMoreRef.current) {
          fetchPosts(
            pageRef.current + 1,
            true,
            activeCategoryRef.current,
            activeHashtagRef.current,
          );
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, [fetchPosts]);

  useEffect(() => {
    // Reset paging when filters change
    pageRef.current = 1;
    hasMoreRef.current = true;
    activeCategoryRef.current = activeCategory;
    activeHashtagRef.current = activeHashtag;
    activeBucketRef.current = activeBucket;
    fetchPosts(1, false, activeCategory, activeHashtag);
    // ── Defer non-critical secondary fetches by 500ms so the primary feed
    // request gets exclusive HTTP/2 stream priority on slow networks.
    // Previously all four ran in parallel on mount, causing the browser to
    // queue them behind the primary feed and cancel everything on the
    // first re-render. Mar 2026 production-perf hotfix.
    const t = setTimeout(() => {
      axios.get(`${API}/hashtags/trending`).then(res => setTrendingTags(res.data || [])).catch(() => {});
      if (user) {
        axios.get(`${API}/social/suggestions`).then(res => setSuggestions(res.data?.slice(0, 5) || [])).catch(() => {});
      }
      if (user && user.role === 'student') {
        axios.get(`${API}/workflow/profile-status`)
          .then(res => {
            setHasApplication(res.data.has_application);
          })
          .catch(() => setHasApplication(false));
      }
    }, 500);
    return () => clearTimeout(t);
  }, [user, fetchPosts, activeCategory, activeHashtag, activeBucket]);

  // ─── New-posts polling (Feb 2026) ───────────────────────────────────
  // Poll page=1 every 60s with the SAME filters as the visible feed. If
  // any post_ids surface that aren't already on screen, show a Twitter/X
  // style toast: "N new posts — tap to refresh". User decides when to
  // load — we NEVER auto-scroll or auto-prepend (jarring on a feed the
  // user is actively reading).
  //
  // Guardrails:
  //  • Only polls when tab is visible (saves ~95% of network calls in
  //    background tabs).
  //  • Pauses while the initial feed is still loading or while pagination
  //    is mid-flight — re-checking pos1 during a load-more would race.
  //  • Skips if a toast is already showing (don't spam re-renders).
  //  • Stale-toast TTL: 10 minutes — toast auto-clears if user never taps.
  const newPostsBufferRef = useRef([]);  // staged posts to prepend on tap
  const newPostsToastIdRef = useRef(null);

  const loadNewPosts = useCallback(() => {
    if (newPostsBufferRef.current.length === 0) return;
    setPosts(prev => {
      const seen = new Set(prev.map(p => p.post_id));
      const fresh = newPostsBufferRef.current.filter(p => !seen.has(p.post_id));
      return [...fresh, ...prev];
    });
    newPostsBufferRef.current = [];
    if (newPostsToastIdRef.current) {
      toast.dismiss(newPostsToastIdRef.current);
      newPostsToastIdRef.current = null;
    }
    // Smooth-scroll to the top so the user actually sees the fresh posts.
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_e) { /* no-op on SSR/old browsers */ }
  }, []);

  useEffect(() => {
    let interval = null;

    const checkForNew = async () => {
      if (loading || loadingMoreRef.current) return;
      try {
        let url = `${API}/posts?page=1&limit=10`;
        if (activeCategoryRef.current) url += `&category=${activeCategoryRef.current}`;
        if (activeHashtagRef.current) url += `&hashtag=${activeHashtagRef.current}`;
        if (activeBucketRef.current && activeBucketRef.current !== 'all') {
          url += `&bucket=${activeBucketRef.current}`;
        }
        const res = await axios.get(url, { timeout: 8000 });
        const incoming = res.data?.posts || [];
        if (!incoming.length) return;

        // Compare against what the user CURRENTLY sees. Use the functional
        // setter to grab the latest posts without listing them in deps
        // (which would re-create the interval on every like/comment).
        setPosts(currentPosts => {
          const seen = new Set(currentPosts.map(p => p.post_id));
          const fresh = incoming.filter(p => !seen.has(p.post_id));
          if (fresh.length === 0) return currentPosts;
          newPostsBufferRef.current = fresh;
          if (newPostsToastIdRef.current) {
            toast.dismiss(newPostsToastIdRef.current);
          }
          newPostsToastIdRef.current = toast(
            `${fresh.length} new ${fresh.length === 1 ? 'post' : 'posts'} — tap to view`,
            {
              duration: 10 * 60 * 1000,  // 10 min TTL
              action: { label: 'Show', onClick: loadNewPosts },
              dismissible: true,
              position: 'top-center',
            },
          );
          return currentPosts;  // don't mutate the list — user opts in via toast
        });
      } catch (_e) { /* network blip — try again next tick */ }
    };

    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (document.visibilityState === 'visible') checkForNew();
      }, 60_000);
    };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } };
    const onVis = () => {
      if (document.visibilityState === 'visible') { checkForNew(); start(); }
      else stop();
    };

    start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      if (newPostsToastIdRef.current) {
        toast.dismiss(newPostsToastIdRef.current);
        newPostsToastIdRef.current = null;
      }
    };
  }, [loading, loadNewPosts]);

  // Whenever filters change, drop the staged buffer + toast — they were
  // collected against the OLD filter set. Refs only (no setState) so the
  // react-hooks/set-state-in-effect lint stays clean.
  useEffect(() => {
    newPostsBufferRef.current = [];
    if (newPostsToastIdRef.current) {
      toast.dismiss(newPostsToastIdRef.current);
      newPostsToastIdRef.current = null;
    }
  }, [activeCategory, activeHashtag, activeBucket]);

  const handleLike = async (postId) => {
    if (!user) { navigate('/auth'); return; }
    try {
      const res = await axios.post(`${API}/posts/${postId}/like`, {});
      setPosts(prev => prev.map(p =>
        p.post_id === postId
          ? { ...p, like_count: p.like_count + (res.data.liked ? 1 : -1), likes: res.data.liked ? [...(p.likes || []), user.user_id] : (p.likes || []).filter(l => l !== user.user_id) }
          : p
      ));
    } catch { toast.error('Failed to like'); }
  };

  const handleBookmark = async (postId) => {
    if (!user) { navigate('/auth'); return; }
    try {
      const res = await axios.post(`${API}/posts/${postId}/bookmark`, {});
      setPosts(prev => prev.map(p =>
        p.post_id === postId
          ? { ...p, bookmarks: res.data.bookmarked ? [...(p.bookmarks || []), user.user_id] : (p.bookmarks || []).filter(b => b !== user.user_id) }
          : p
      ));
      toast.success(res.data.bookmarked ? 'Saved' : 'Unsaved');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (postId) => {
    // Optimistic UI — remove the card immediately so the user sees instant
    // feedback even on slow connections (Atlas RTT is ~190ms). On failure
    // we put it back and surface an error.
    const snapshot = posts;
    setPosts(prev => prev.filter(p => p.post_id !== postId));
    try {
      await axios.delete(`${API}/posts/${postId}`);
      toast.success('Post deleted');
    } catch (e) {
      setPosts(snapshot);
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleEditSave = (postId, newContent) => {
    setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, content: newContent } : p));
  };

  const handleRepost = async (postId) => {
    try {
      const res = await axios.post(`${API}/posts/${postId}/repost`, { quote: '' });
      toast.success('Reposted!');
      // Refresh feed to show the repost
      const feedRes = await axios.get(`${API}/posts?page=1&limit=20`);
      setPosts(feedRes.data.posts || feedRes.data);
    } catch (e) { toast.error('Failed to repost'); }
  };

  return (
    <DesktopLayout>
      <div data-testid="feed-page">
      <SEOHead
        title="Flow — What's Happening in Study Abroad"
        description="Stay updated with the latest courses, scholarships, visa news and opportunities for international students in Australia. Go with the Flow on Unexa."
        path="/flow"
      />
      {/* Create Post Box */}
      {isAllowed('social_create_post') && <div
        className="glass-card p-3 mb-4 flex items-center gap-3 cursor-pointer active:scale-[0.99] transition-transform"
        onClick={() => {
          if (!user) { navigate('/auth'); return; }
          // Agents get the dedicated AgentComposeModal (Consultation Offer /
          // News / Success Story / Tip / Event) inline — students &
          // institutions continue to /create (CreatePostPage).
          if (user.role === 'agent') { setShowAgentCompose(true); return; }
          navigate('/create');
        }}
        data-testid="create-post-box"
      >
        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0 overflow-hidden">
          {user?.profile_picture ? (
            <img src={resolveImageUrl(user.profile_picture)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-bold text-[#2F80ED]">{user?.name?.[0]?.toUpperCase() || '?'}</span>
          )}
        </div>
        <div className="flex-1 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center px-4">
          <span className="text-sm text-slate-400">What's on your mind?</span>
        </div>
        <button className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#2F80ED] hover:bg-blue-100 transition-all" data-testid="create-post-btn">
          <Image className="h-5 w-5" />
        </button>
      </div>}

      {/* Live Tracking Card */}
      <LiveTrackingCard />

      {/* Journey Banner — Mobile only */}
      {user && (
        <div className="lg:hidden mb-4 bg-gradient-to-r from-[#2F80ED] to-[#2F80ED] rounded-2xl p-4 relative overflow-hidden" data-testid="feed-journey-banner">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <p className="text-blue-200 text-[10px] font-bold uppercase tracking-widest">Select Your Journey</p>
          <p className="text-white font-bold text-base mt-1">What are you looking for?</p>
          <div className="flex gap-2 mt-3">
            {[
              {label: 'Admission', type: 'admission', flagKey: 'courses'},
              {label: 'Visa', type: 'visa', flagKey: 'visa'},
              {label: 'More', type: 'more'},
            ].filter(b => !b.flagKey || isEnabled(b.flagKey)).map(({label, type}) => (
              <button key={label} onClick={() => type === 'more' ? navigate('/explore') : applyCtx?.triggerApply(type)} className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${label === 'Admission' ? 'bg-white text-[#0A2540] hover:bg-blue-50/50' : 'bg-white/15 text-white hover:bg-white/25 border border-white/20'}`} data-testid={`feed-journey-${label.toLowerCase()}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stories — HIDDEN per request, kept in code for future re-enable */}
      {false && (
      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 lg:mx-0 lg:px-0">
        {MOCK_STORIES.map((story) => (
          <button key={story.id} className="flex flex-col items-center gap-1 shrink-0" data-testid={`story-${story.id}`}>
            <div className={story.isOwn ? 'p-[2px] rounded-full border-2 border-dashed border-slate-300' : 'story-ring'}>
              <img src={story.img} alt="" className="w-14 h-14 rounded-full object-cover" />
            </div>
            <span className="text-[10px] text-slate-500 max-w-[60px] truncate">{story.name}</span>
          </button>
        ))}
      </div>
      )}

      {/* Ad Carousel */}
      <AdCarousel />

      {/* Flow filter buckets — student-facing 7-pill row.
          Sends `&bucket=<id>` server-side. All=navy, Scholarship=amber,
          others=teal. Old composer-category pills (Share/Tips/Ask/etc.)
          intentionally removed — those map to post creation, not feed
          filtering. Horizontal scroll with a soft right-edge fade. */}
      <div className="relative -mx-4 px-4 lg:mx-0 lg:px-0">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2" data-testid="flow-filter-row">
          {FLOW_FILTERS.map((f) => {
            const isActive = activeBucket === f.id;
            const baseClass = 'shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-all active:scale-95 whitespace-nowrap';
            // Pick active palette per spec.
            let activeStyle = null;
            if (isActive) {
              if (f.id === 'all') activeStyle = { backgroundColor: '#0F2240', color: '#FFFFFF', borderColor: '#0F2240' };
              else if (f.scholar) activeStyle = { backgroundColor: '#F5A623', color: '#0F2240', borderColor: '#F5A623' };
              else activeStyle = { backgroundColor: '#1A7A6E', color: '#FFFFFF', borderColor: '#1A7A6E' };
            }
            // Scholarship gets a soft amber resting tint to draw attention.
            const idleStyle = !isActive && f.scholar
              ? { backgroundColor: 'rgba(245,166,35,0.08)', color: '#92400E', borderColor: '#F5A623' }
              : !isActive ? { backgroundColor: '#FFFFFF', color: '#475569', borderColor: '#e2e8f0' } : null;
            return (
              <button
                key={f.id}
                onClick={() => {
                  // Tapping the active pill returns to "all" per spec.
                  setActiveBucket(isActive ? 'all' : f.id);
                  setPage(1);
                }}
                style={isActive ? activeStyle : idleStyle}
                className={baseClass}
                data-testid={`flow-pill-${f.id}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        {/* Soft right-edge fade — visual nudge that more pills exist when
            the row overflows on narrow screens. */}
        <div className="pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-white to-transparent lg:hidden" />
      </div>

      {/* Flow search bar — role-scoped lead-discovery search.
          Visible only for agents + institutions (students/recruiters hidden).
          Search pool per role:
            • Agent       → posts with category='visa_experience'
            • Institution → posts with category in ['looking_for','admissions']
      {/* Wired Mar 1, 2026 — purely client-side filter on already-fetched data.
          Visibility is gated by the agent/institution feature flag
          (`agent_find_leads` / `inst_find_leads`) so admins can toggle the
          whole row on/off per country from Admin → Feature Flags → Agents
          (or Institutions) → Core. Find-leads is OFF when:
            • the role-scoped flag is disabled by admin, OR
            • the user is suspended on that specific flag.
          IMPORTANT: We also gate on `featuresLoaded` so the row never flashes
          on first paint before /api/my-features resolves — otherwise an
          admin-disabled flag would leak visible for ~1 frame. */}
      {featuresLoaded && user && (
        (user.role === 'agent' && isAllowed('agent_find_leads')) ||
        (user.role === 'institution' && isAllowed('inst_find_leads'))
      ) && (
      <div className="flex items-center gap-2 mb-3" data-testid="flow-search-row">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              const v = e.target.value;
              setSearchInput(v);
              // Debounced commit so we don't filter on every keystroke
              clearTimeout(window.__flowSearchTimer);
              window.__flowSearchTimer = setTimeout(() => setSearchQuery(v.trim()), 250);
            }}
            placeholder={user.role === 'agent'
              ? 'Search student visa leads (visa, PR, migration)…'
              : 'Search student course leads (course, MBA, admission)…'}
            className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-full text-sm text-[#0F2240] placeholder-slate-400 focus:outline-none focus:border-[#2F80ED] focus:ring-2 focus:ring-[#2F80ED]/15"
            data-testid="flow-search-input"
          />
          {searchInput && (
            <button
              onClick={() => { setSearchInput(''); setSearchQuery(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center"
              data-testid="flow-search-clear"
            >
              <X className="h-3.5 w-3.5 text-slate-400" />
            </button>
          )}
        </div>
        <button
          onClick={() => setFindLeadsActive(v => !v)}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold active:scale-95 transition-all ${
            findLeadsActive
              ? 'bg-[#0F2240] text-white shadow-lg shadow-slate-900/20 ring-2 ring-[#1A7A6E]/40'
              : 'bg-[#1A7A6E] text-white hover:bg-[#155f56]'
          }`}
          data-testid="find-leads-cta"
          aria-pressed={findLeadsActive}
        >
          <Briefcase className="h-3.5 w-3.5" /> {findLeadsActive ? 'Showing leads' : 'Find leads'}
        </button>
      </div>
      )}

      {/* Follow Suggestions — Mobile only */}
      {isAllowed('social_suggestions') && suggestions.length > 0 && (
        <div className="lg:hidden mb-4 bg-white rounded-2xl border border-slate-100 p-4" data-testid="suggestions-card">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Suggested for You</p>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
            {suggestions.map(s => (
              <div key={s.user_id} className="shrink-0 flex flex-col items-center gap-1.5 w-20 cursor-pointer" onClick={() => navigate(`/user/${s.user_id}`)} data-testid={`suggest-${s.user_id}`}>
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center overflow-hidden">
                  {s.profile_picture ? <img src={resolveImageUrl(s.profile_picture)} alt="" className="w-full h-full object-cover" /> :
                    <span className="text-sm font-bold text-[#2F80ED]">{s.name?.[0]?.toUpperCase()}</span>}
                </div>
                <p className="text-[10px] font-semibold text-[#0A2540] truncate w-full text-center hover:text-[#2F80ED] transition-colors">{s.name?.split(' ')[0]}</p>
                <p className="text-[9px] text-slate-400 truncate w-full text-center">{s.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flow Live Card — hidden per Feb 27 user request. Re-enable by
          uncommenting the line below.
      <FlowLiveCard /> */}

      {/* Posts */}
      {loading ? (
        <div className="space-y-4 pb-4">
          {[1, 2, 3].map(i => <PostSkeleton key={i} />)}
        </div>
      ) : (() => {
        // Flow search filter (Mar 1, 2026) — matches across title / body /
        // tags / category / country / hashtags for both intel cards AND posts.
        // Empty query = pass-through.
        const q = (searchQuery || '').trim().toLowerCase();
        const matches = (haystack) => !q || (haystack || '').toLowerCase().includes(q);
        const intelMatch = (i) => matches(
          [i.title, i.summary, i.headline, i.category, i.country, i.country_code,
           (i.tags || []).join(' '), (i.hashtags || []).join(' ')].filter(Boolean).join(' ')
        );
        // Role-scoped search pool — search results are restricted to the
        // student-post categories relevant to the viewer's role. Students
        // and recruiters never see the search bar (gated above), so they
        // get no scoping here.
        //   Agent       → ['visa_experience']                fuzzy keywords: visa, pr, migration
        //   Institution → ['looking_for', 'admissions']      fuzzy keywords: course, mba, degree, admission, enroll
        const role = user?.role;
        const SEARCH_POOL = role === 'agent'
          ? new Set(['visa_experience'])
          : role === 'institution'
          ? new Set(['looking_for', 'admissions'])
          : null;
        const postMatch = (p) => {
          if (!q) return true;
          // Pool gate — exclude any post outside the viewer's role pool.
          if (SEARCH_POOL && !SEARCH_POOL.has(p.category)) return false;
          // Substring match across content / tags / location (per spec).
          return matches(
            [p.content, p.body, p.title, p.location,
             (p.tags || []).join(' '), (p.hashtags || []).join(' ')]
              .filter(Boolean).join(' ')
          );
        };
        const filteredIntel = q ? (intelItems || []).filter(intelMatch) : (intelItems || []);
        let filteredPosts = q ? (posts || []).filter(postMatch) : (posts || []);

        // Find-leads in-place filter — restricts feed to student lead-intent
        // posts only. Stacks with search query (both apply simultaneously).
        const LEAD_CATEGORIES = new Set(['looking_for', 'visa_experience', 'admissions']);
        if (findLeadsActive) {
          filteredPosts = filteredPosts.filter(p => LEAD_CATEGORIES.has(p.category));
        }

        // ── Cross-source dedup (Jun 29, 2026) ────────────────────────────
        // `useFeedIntelligence` fetches raw news/blog articles from
        // `/api/blog/posts?content_type=news|blog`, and the Flow mixer
        // ALSO returns their mirror posts (source=news_auto_publish /
        // blog_auto_publish) via `/api/posts`. Without dedup, the same
        // article renders twice — once as an intel card (real cover, real
        // read-time) and once as a mirror card (different mirror_cover_image
        // + mirror_read_time). Users see it as "duplicated news and blog".
        // Rule: if an intel item exists for a given article_id, drop the
        // mirror post from the feed; keep the intel item (it has the
        // canonical cover + metadata).
        //
        // BELT-AND-SUSPENDERS (Jun 29, 2026 — iter_115 race fix):
        // Even before intelItems finishes loading, PROACTIVELY drop posts
        // whose `source` is `news_auto_publish` or `blog_auto_publish`.
        // These posts exist SOLELY to mirror a news/blog article and will
        // be superseded by the canonical intel card the moment intelItems
        // arrives. Rendering them as intel cards via mirrorToIntel during
        // the fetch window causes duplicate testids + wrong priority.
        // Dropping them by source is safe: no other UI depends on them.
        const MIRROR_SOURCES = new Set(['news_auto_publish', 'blog_auto_publish']);
        filteredPosts = filteredPosts.filter(p =>
          !(p && p.source && MIRROR_SOURCES.has(p.source))
        );
        const intelArticleIds = new Set(
          (intelItems || []).map(i => i && i.article_id).filter(Boolean)
        );
        if (intelArticleIds.size > 0) {
          filteredPosts = filteredPosts.filter(p => {
            const aid = p && p.mirrored_from_article_id;
            return !aid || !intelArticleIds.has(aid);
          });
        }

        // When search or find-leads is active, suppress intel + sponsored cards
        // entirely — agents only want student posts in this mode.
        const studentPostsOnly = !!q || findLeadsActive;

        // News and Guides & Q&A buckets surface the Newsboard / Blog / Q&A
        // intel cards as primary content (the orange "Read full update →"
        // cards from useFeedIntelligence). Other buckets keep the legacy
        // every-3rd-slot injection.
        const newsIntelCards = activeBucket === 'news' && !studentPostsOnly
          ? filteredIntel.filter(i => i.type === 'news')
          : [];
        const blogIntelCards = activeBucket === 'blogs' && !studentPostsOnly
          ? filteredIntel.filter(i => i.type === 'blog')
          : [];
        const qaIntelCards = activeBucket === 'qa' && !studentPostsOnly
          ? filteredIntel.filter(i => i.type === 'qa')
          : [];
        const primaryIntelCards = newsIntelCards.length ? newsIntelCards
          : blogIntelCards.length ? blogIntelCards
          : qaIntelCards.length ? qaIntelCards
          : [];
        const suppressInlineIntel = primaryIntelCards.length > 0;
        // ── All bucket age windows + evergreen fallback (Jun 29, 2026) ─
        //   User-defined priority order (newest first):
        //     1. Fresh news    <72h         (highest priority)
        //     2. Fresh blogs   <7d
        //     3. Recent news   72h – 30d
        //     4. Recent blogs  7d – 90d
        //     5. Evergreen     older than the recent windows (fallback)
        //   Ring structure and slot semantics (Mar 1, 2026) are preserved
        //   as-is; only the pool composition + fallback order changes so
        //   the feed is never empty when there is content available.
        const isAllBucket = activeBucket === 'all' && !studentPostsOnly;
        const allNewsItems = filteredIntel.filter(i => i.type === 'news');
        const allBlogItems = filteredIntel.filter(i => i.type === 'blog');
        const SEVENTY2H  = 72 * 60 * 60 * 1000;                //  72 hours
        const SEVEN_D    = 7  * 24 * 60 * 60 * 1000;           //   7 days
        const THIRTY_D   = 30 * 24 * 60 * 60 * 1000;           //  30 days
        const NINETY_D   = 90 * 24 * 60 * 60 * 1000;           //  90 days
        const now = Date.now();
        const ageMs = (it) => {
          const ca = it && (it.created_at || it.published_at);
          if (!ca) return Infinity;
          return now - new Date(ca).getTime();
        };
        // Age classifiers per the priority ladder
        const isNewsFresh   = (it) => ageMs(it) <= SEVENTY2H;                       // <72h
        const isBlogFresh   = (it) => ageMs(it) <= SEVEN_D;                         // <7d
        const isNewsRecent  = (it) => { const a = ageMs(it); return a > SEVENTY2H && a <= THIRTY_D; };  // 72h–30d
        const isBlogRecent  = (it) => { const a = ageMs(it); return a > SEVEN_D    && a <= NINETY_D; }; // 7d–90d
        const pinnedIds = new Set();   // Mar 1, 2026 — pin strip removed; 9-slot ring is the only feed source
        const shownArticleIds = new Set();  // running dedup tracker
        // ── isEmpty gate (Jun 29, 2026 fix) ─────────────────────────────
        // In the "All" bucket, the 9-slot flow ring pulls from BOTH student
        // posts (slots 4, 8) AND intel news/blog cards (slots 1/3/5/7/9).
        // The cross-source dedup (iteration_112) can legitimately reduce
        // `filteredPosts` to 0 when every mirror maps to an intel item —
        // in that case the feed is NOT empty (intel cards still render).
        // Only treat the feed as empty when NONE of the three sources has
        // anything to show.
        const hasIntelForAllBucket = isAllBucket && (
          allNewsItems.length > 0 || allBlogItems.length > 0
        );
        const isEmpty = filteredPosts.length === 0
                      && primaryIntelCards.length === 0
                      && !hasIntelForAllBucket;

        // ── 9-SLOT FLOW RING (All bucket only, Mar 1, 2026) ──────────────
        // Ring structure UNCHANGED. Only pool composition changed to add
        // evergreen fallback + widened news/blog age windows.
        //   1=News 2=(skip) 3=Blog 4=Post 5=News 6=(skip) 7=Blog
        //   8=Post 9=News-fallback  → repeat
        // Slots 2/6 (scholarship/agent offer) + 8-sponsored are skipped
        // gracefully because those pools are not fetched on this page yet.
        // Wires only existing data sources: intelItems + posts[].
        const flowItems = [];
        if (isAllBucket) {
          // Tier 1 — Fresh news (<72h), sorted by relevance score desc.
          const newsFreshQ = allNewsItems
            .filter(n => !pinnedIds.has(n.article_id) && isNewsFresh(n))
            .sort((a, b) => Number(b.relevance_score || 0) - Number(a.relevance_score || 0));
          // Tier 2 — Fresh blogs (<7d), newest first.
          const blogFreshQ = allBlogItems
            .filter(b => !pinnedIds.has(b.article_id) && isBlogFresh(b))
            .sort((a, b) => ageMs(a) - ageMs(b));
          // Tier 3 — Recent news (72h–30d), newest first.
          const newsRecentQ = allNewsItems
            .filter(n => !pinnedIds.has(n.article_id) && isNewsRecent(n))
            .sort((a, b) => ageMs(a) - ageMs(b));
          // Tier 4 — Recent blogs (7d–90d), newest first.
          const blogRecentQ = allBlogItems
            .filter(b => !pinnedIds.has(b.article_id) && isBlogRecent(b))
            .sort((a, b) => ageMs(a) - ageMs(b));
          // Tier 5 — Evergreen (anything older than the recent windows).
          // Kept separate for news vs blog so slot preference is preserved.
          const newsEvergreenQ = allNewsItems
            .filter(n => !pinnedIds.has(n.article_id) && ageMs(n) > THIRTY_D)
            .sort((a, b) => ageMs(a) - ageMs(b));
          const blogEvergreenQ = allBlogItems
            .filter(b => !pinnedIds.has(b.article_id) && ageMs(b) > NINETY_D)
            .sort((a, b) => ageMs(a) - ageMs(b));
          const postQ = filteredPosts.slice();
          const popUnique = (q) => {
            while (q.length) {
              const it = q.shift();
              if (!it.article_id || !shownArticleIds.has(it.article_id)) {
                if (it.article_id) shownArticleIds.add(it.article_id);
                return it;
              }
            }
            return null;
          };
          // Slot pickers implement the priority ladder. News-preferred
          // slots (1, 5) walk news tiers before falling back to blog
          // tiers; blog-preferred slots (3, 7) do the reverse. Slot 9
          // is the "fallback" slot that picks the freshest remaining
          // item across all tiers.
          const pickNewsSlot = () =>
            popUnique(newsFreshQ)    || popUnique(blogFreshQ)   ||
            popUnique(newsRecentQ)   || popUnique(blogRecentQ)  ||
            popUnique(newsEvergreenQ)|| popUnique(blogEvergreenQ);
          const pickBlogSlot = () =>
            popUnique(blogFreshQ)    || popUnique(newsFreshQ)   ||
            popUnique(blogRecentQ)   || popUnique(newsRecentQ)  ||
            popUnique(blogEvergreenQ)|| popUnique(newsEvergreenQ);
          const pickFallbackSlot = () =>
            popUnique(newsRecentQ)   || popUnique(blogRecentQ)  ||
            popUnique(newsFreshQ)    || popUnique(blogFreshQ)   ||
            popUnique(newsEvergreenQ)|| popUnique(blogEvergreenQ);
          const anyIntelLeft = () => (
            newsFreshQ.length || blogFreshQ.length ||
            newsRecentQ.length || blogRecentQ.length ||
            newsEvergreenQ.length || blogEvergreenQ.length
          );
          let slot = 0;
          let safety = 0;
          while ((postQ.length || anyIntelLeft()) && safety < 500) {
            const pos = (slot % 9) + 1;
            slot += 1; safety += 1;
            let pick = null;
            if (pos === 1 || pos === 5) {
              pick = pickNewsSlot();
              if (pick) flowItems.push({ kind: 'intel', payload: pick });
            } else if (pos === 3 || pos === 7) {
              pick = pickBlogSlot();
              if (pick) flowItems.push({ kind: 'intel', payload: pick });
            } else if (pos === 4 || pos === 8) {
              if (postQ.length) flowItems.push({ kind: 'post', payload: postQ.shift() });
            } else if (pos === 9) {
              pick = pickFallbackSlot();
              if (pick) flowItems.push({ kind: 'intel', payload: pick });
            }
            // pos 2 and 6 are skipped gracefully (no pool fetched on Flow)
          }
        }

        if (isEmpty) {
          // Per-bucket empty state — message + optional CTA per spec.
          // Search and find-leads filters override the bucket-specific msg.
          const active = FLOW_FILTERS.find(f => f.id === activeBucket);
          const isAll = !active || active.id === 'all';
          let msg = isAll ? 'No posts yet. Be the first to post!' : active.empty;
          if (studentPostsOnly) {
            if (q && findLeadsActive) msg = `No student leads matching "${searchQuery}" yet.`;
            else if (q) msg = `No student posts matching "${searchQuery}" yet.`;
            else if (findLeadsActive) msg = 'No student leads yet. Check back soon — students post new requests every day.';
          }
          return (
            <div className="text-center py-12 px-6" data-testid={`empty-state-${activeBucket}`}>
              <p className="text-sm text-slate-500 leading-relaxed max-w-sm mx-auto">{msg}</p>
              {!studentPostsOnly && active?.emptyCta && (
                <button
                  onClick={() => navigate(active.emptyCta.to)}
                  className="mt-4 px-4 py-2 rounded-full bg-[#1A7A6E] text-white text-xs font-bold active:scale-95 transition-all"
                  data-testid={`empty-cta-${activeBucket}`}
                >
                  {active.emptyCta.label}
                </button>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-4 pb-4">
            {/* ─── Mirror-to-intel adapter (Feb 2026) ──────────────────
                Posts authored by the system Unexa user with
                source ∈ {news_auto_publish, blog_auto_publish} are
                mirrors of news_articles. Render them as rich
                NewsCard/BlogCard (same look as the Blogs pill) rather
                than the plain text PostCard. The adapter maps mirror_*
                fields back to the FeedIntelCard's item shape.
            */}
            {/* Bucket-pinned intel cards — News pill pins newsboard cards
                from /blog/posts?content_type=news, Guides & Q&A pins blog
                + Q&A items. Renders before posts. */}
            {primaryIntelCards.map((item, i) => (
              <div key={`primary-intel-${item.article_id || item.question_id || i}`} data-testid={`primary-intel-${i}`}>
                <FeedIntelCard item={item} />
              </div>
            ))}
            {/* All bucket: 9-slot flow ring. Non-All buckets: legacy every-3rd intel. */}
            {isAllBucket ? flowItems.map((item, idx) => {
              if (item.kind === 'post') {
                const post = item.payload;
                // Student-post-only index drives ad cadence (every 4th post).
                const studentIdx = flowItems.slice(0, idx + 1).filter(x => x.kind === 'post').length - 1;
                const showAd = (studentIdx + 1) % 4 === 0;
                const mirror = mirrorToIntel(post);
                return (
                  <div key={`flow-post-${post.post_id}`}>
                    {mirror ? (
                      <FeedIntelCard item={mirror} />
                    ) : (
                      <PostCard
                        post={post}
                        onLike={handleLike}
                        onBookmark={handleBookmark}
                        onDelete={handleDelete}
                        onEditSave={handleEditSave}
                        onRepost={handleRepost}
                        userId={user?.user_id}
                        isAllowed={isAllowed}
                        currentUserRole={user?.role}
                      />
                    )}
                    {showAd && (
                      <div className="mt-4">
                        <InFeedAd index={Math.floor(studentIdx / 4)} />
                      </div>
                    )}
                  </div>
                );
              }
              const intel = item.payload;
              return (
                <div key={`flow-intel-${intel.article_id || idx}`} data-testid={`flow-intel-${idx}`}>
                  <FeedIntelCard item={intel} />
                </div>
              );
            }) : filteredPosts.map((post, idx) => {
              // Legacy every-3rd intel for non-All buckets (Blogs/QA off-pill).
              // Disabled when search/find-leads is active — student posts only.
              let intelItem = null;
              let intelSlotKey = -1;
              if (!studentPostsOnly && !suppressInlineIntel && (idx + 1) % 3 === 0) {
                const intelSlot = Math.floor((idx + 1) / 3) - 1;
                if (intelItems[intelSlot % Math.max(1, intelItems.length)]) {
                  intelItem = intelItems[intelSlot % Math.max(1, intelItems.length)];
                  intelSlotKey = intelSlot;
                }
              }
              const showAd = !studentPostsOnly && (idx + 1) % 4 === 0;
              const mirror = mirrorToIntel(post);
              return (
                <div key={post.post_id}>
                  {mirror ? (
                    <FeedIntelCard item={mirror} />
                  ) : (
                    <PostCard
                      post={post}
                      onLike={handleLike}
                      onBookmark={handleBookmark}
                      onDelete={handleDelete}
                      onEditSave={handleEditSave}
                      onRepost={handleRepost}
                      userId={user?.user_id}
                      isAllowed={isAllowed}
                      currentUserRole={user?.role}
                    />
                  )}
                  {showAd && (
                    <div className="mt-4">
                      <InFeedAd index={Math.floor(idx / 4)} />
                    </div>
                  )}
                  {intelItem && (
                    <div className="mt-4" data-testid={`feed-intel-slot-${intelSlotKey}`}>
                      <FeedIntelCard item={intelItem} />
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={sentinelRef} className="h-1" />
            {loadingMore && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 text-[#2F80ED] animate-spin" />
              </div>
            )}
            {!hasMore && filteredPosts.length > 0 && (
              <p className="text-center text-xs text-slate-300 py-4">You've reached the end</p>
            )}
          </div>
        );
      })()}
      </div>

      {/* Agent compose modal — Flow page entry. Same modal used on the
          agent's own public profile, but opened inline from Flow so agents
          never see the student "Looking for / Visa / Admissions" composer. */}
      {showAgentCompose && user?.role === 'agent' && (
        <AgentComposeModal
          agent={user}
          onClose={() => setShowAgentCompose(false)}
          onPosted={(newPost) => {
            setShowAgentCompose(false);
            if (newPost) setPosts((prev) => [newPost, ...prev]);
          }}
        />
      )}
    </DesktopLayout>
  );
}

// Shared post card — exported so profile pages can render posts with the
// same look as the Flow feed. Pass `hideProfileCTA` when the viewer is
// already on the author's profile (avoids redundant "View Profile" loop).
export function PostCard({ post, onLike, onBookmark, onDelete, onEditSave, onRepost, userId, isAllowed = () => true, currentUserRole = 'student', hideProfileCTA = false }) {
  const navigate = useNavigate();
  const { gate, sheetOpen, sheetTitle, setSheetOpen, returnUrl } = useGuestGate();
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  // Announcement read-more: long institution announcements get truncated to
  // 280 chars + a "Read More →" toggle. Short announcements skip the gate.
  const [expandedAnnouncement, setExpandedAnnouncement] = useState(false);
  // Claim-Offer modal — opened by the "Claim This Offer →" CTA on
  // special_offer institution cards.
  const [showClaimOffer, setShowClaimOffer] = useState(false);
  // Free Consultation modal — opens from the "Book Consultation" CTA on
  // agent free_consultation cards. Routes through /agents/:id/request-consult
  // (free, opens agent inbox + chat thread on accept). No credits deducted.
  const [showBookConsult, setShowBookConsult] = useState(false);

  // ─── Lead Card detection ───
  // Looking For / Visa / Admissions posts get a distinct purple treatment
  // and expose a Connect button (300 credits) for agents/institutions only.
  const LEAD_META = {
    looking_for: { label: '🔍 LOOKING FOR' },
    visa_experience: { label: '✈️ VISA QUERY' },
    admissions: { label: '🎓 ADMISSIONS' },
  };
  const isLead = LEAD_META[post.category];
  const { isHidden } = useActionStates();
  // Admin-gated buyer actions — disabling these via Credit Engine removes
  // the buttons platform-wide AND prevents charging (backend defence).
  const connectHidden = isHidden('connect_to_lead');
  const claimHidden = isHidden('claim_offer');
  const consultHidden = isHidden('book_consultation');
  const canConnect = isLead && !post.is_system && !connectHidden && (currentUserRole === 'agent' || currentUserRole === 'institution');

  // ─── Agent Post detection ───
  // All agent posts get a teal ribbon. free_consultation posts override with
  // an amber "Free Consultation Offer" ribbon + amber CTA button.
  const isAgentPost = post.user_role === 'agent' && !post.is_system;
  const isFreeConsult = isAgentPost && post.category === 'free_consultation';

  // ─── Institution Post detection ───
  // Institution posts get a per-category ribbon, a CRICOS · city meta line,
  // and a per-category CTA pointing at the institution profile.
  const isInstPost = post.user_role === 'institution' && !post.is_system;
  const isInstOffer = isInstPost && post.category === 'special_offer';
  const hasOfferPrice = !!(post.post_metadata?.original_fee && post.post_metadata?.offer_price);

  const liked = post.likes?.includes(userId);
  const bookmarked = post.bookmarks?.includes(userId);
  const isOwner = post.user_id === userId;
  const images = (post.images?.length ? post.images : (post.image_url ? [post.image_url] : [])).map(resolveImageUrl).filter(Boolean);

  const handleProfileClick = () => {
    if (post.user_role === 'institution' && post.university_id) {
      navigate(`/university/${post.university_id}`, { state: { fromDirectory: true } });
    } else if (post.user_role === 'agent') {
      navigate(`/agents/${post.user_id}`, { state: { fromDirectory: true } });
    } else if (post.user_id) {
      navigate(`/user/${post.user_id}`);
    }
  };

  const [followLoading, setFollowLoading] = useState(false);

  const handleFollow = async () => {
    setFollowLoading(true);
    try {
      await axios.post(`${API}/social/follow/${post.user_id}`);
      toast.success(`Following ${post.user_name}!`);
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.detail || 'Failed to follow';
      toast.info(msg);
    } finally { setFollowLoading(false); setMenuOpen(false); }
  };

  const handleSendDM = async () => {
    setMenuOpen(false);
    try {
      const res = await axios.post(`${API}/dm/conversations`, { recipient_id: post.user_id });
      navigate('/chat', { state: { openConversation: res.data?.conversation_id } });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start chat'); }
  };

  const handleBlock = async () => {
    setMenuOpen(false);
    try {
      await axios.post(`${API}/social/block/${post.user_id}`);
      toast.success(`Blocked ${post.user_name}`);
    } catch (e) { toast.error('Failed to block'); }
  };

  const baseUrl = window.location.origin;
  const postUrl = `${baseUrl}/flow?post=${post.post_id}`;

  const handleConnect = async (leadPost) => {
    // Step 1: confirmation modal
    const ok = window.confirm(
      `Connecting costs 300 credits.\n\nA chat will open with ${leadPost.user_name}. Continue?`
    );
    if (!ok) return;
    try {
      const res = await axios.post(`${API}/flow/connect-lead`, { post_id: leadPost.post_id });
      const { conversation_id, credit_balance } = res.data;
      toast.success(`Connected! 300 credits used · ${credit_balance} remaining`);
      navigate('/chat', { state: { openConversation: conversation_id } });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to connect';
      toast.error(msg);
    }
  };

  const handleBookConsultation = (agentPost) => {
    // Opens the structured BookConsultationModal (POST /agents/:id/request-consult)
    // which is FREE for students — no credit deduction. The 300-credit cost is
    // a marketplace fee for Agents/Institutions to Connect with student leads,
    // not for students to reach agents (which would be backwards). Agents
    // already paid by being on the platform; consultation bookings are
    // organic conversions for them.
    setShowBookConsult(true);
  };

  return (
    <article
      className={`feed-post animate-slide-up ${isLead ? 'lead-card-purple' : ''} ${isFreeConsult ? 'agent-card-amber' : (isAgentPost ? 'agent-card-teal' : '')}`}
      data-testid={`post-${post.post_id}`}
      data-lead={isLead ? post.category : undefined}
    >
      {/* Lead Card ribbon (purple) — Looking For / Visa / Admissions */}
      {isLead && (
        <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 px-4 py-2 border-b border-purple-100">
          <span className="text-[10px] font-bold text-purple-700 uppercase tracking-wider">
            {LEAD_META[post.category].label}
          </span>
        </div>
      )}
      {/* Agent Post ribbon — per-category. Consultation Offer is amber-filled
          (loudest), the others are coloured-tinted strips. Legacy visa_update
          posts are merged under "Visa & Migration News". */}
      {!isLead && isAgentPost && (() => {
        const AGENT_RIBBONS = {
          free_consultation: { text: '🤝 Consultation Offer',           bg: '#F5A623', color: '#0F2240', loud: true },
          immigration_news:  { text: '📋 Visa & Migration News',        bg: '#EDE9FE', color: '#6D28D9', border: 'rgba(109,40,217,0.15)' },
          visa_update:       { text: '📋 Visa & Migration News',        bg: '#EDE9FE', color: '#6D28D9', border: 'rgba(109,40,217,0.15)' },
          success_story:     { text: '🏆 Success Story',                bg: '#D1FAE5', color: '#047857', border: 'rgba(4,120,87,0.15)' },
          tip:               { text: '💡 Tip',                          bg: '#FEF3C7', color: '#92400E', border: 'rgba(146,64,14,0.15)' },
          event:             { text: '📅 Event',                        bg: '#FFE4E6', color: '#BE123C', border: 'rgba(190,18,60,0.15)' },
        };
        const r = AGENT_RIBBONS[post.category];
        if (r) {
          return (
            <div
              className="px-4 py-2 border-b"
              style={{ backgroundColor: r.bg, borderBottomColor: r.border || 'rgba(15,34,64,0.15)' }}
            >
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: r.color }}>
                {r.text}
              </span>
            </div>
          );
        }
        // Fallback for uncategorised agent posts — show the verified strip
        return (
          <div className="px-4 py-2 border-b border-teal-700/20" style={{ backgroundColor: '#1A7A6E' }}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-white">
              ✅ Verified MARA Agent
            </span>
          </div>
        );
      })()}

      {/* Institution Post ribbon — per-category. Only renders when the post
          has a recognised institution category (otherwise no ribbon). */}
      {!isLead && isInstPost && (() => {
        const INST_RIBBONS = {
          scholarship:      { text: '🎓 SCHOLARSHIP AVAILABLE', bg: '#F5A623', color: '#0F2240' },
          special_offer:    { text: '💰 EXCLUSIVE OFFER',       bg: '#7C3AED', color: '#FFFFFF' },
          course_spotlight: { text: '📚 COURSE SPOTLIGHT',      bg: '#2563EB', color: '#FFFFFF' },
          announcement:     { text: '📢 INSTITUTION NEWS',      bg: '#374151', color: '#FFFFFF' },
        };
        const r = INST_RIBBONS[post.category];
        if (!r) return null;
        return (
          <div className="px-4 py-2 border-b" style={{ backgroundColor: r.bg, borderBottomColor: 'rgba(15,34,64,0.15)' }} data-testid={`inst-ribbon-${post.post_id}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: r.color }}>
              {r.text}
            </span>
          </div>
        );
      })()}

      {/* Repost indicator */}
      {post.is_repost && (
        <div className="flex items-center gap-1.5 px-4 pt-2 text-caption text-[#65676B]">
          <Repeat2 className="h-3.5 w-3.5" /> <span className="cursor-pointer hover:text-[#2F80ED] transition-colors font-medium" onClick={handleProfileClick}>{post.user_name}</span> reposted
        </div>
      )}
      {/* Header — tighter top padding when sitting directly under a Lead or Agent/Institution ribbon */}
      <div className={`flex items-center justify-between px-4 ${(isLead || isAgentPost || (isInstPost && post.category)) ? 'pt-2 pb-3' : 'py-3'}`}>
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#2F80ED] transition-all"
            onClick={handleProfileClick}
          >
            {post.user_avatar ? (
              <img src={resolveImageUrl(post.user_avatar)} alt="" className="w-full h-full object-cover" data-testid={`post-avatar-${post.post_id}`} />
            ) : (
              <span className="text-sm font-bold text-[#2F80ED]">{post.user_name?.[0]?.toUpperCase()}</span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-username text-[#0A2540] cursor-pointer hover:text-[#2F80ED] transition-colors" onClick={handleProfileClick} data-testid={`post-name-${post.post_id}`}>
                {post.user_name}
              </span>
              {/* Agents always show the MARA verified tick. The "Verified Agent"
                  pill was removed — the ribbon at top already carries that signal. */}
              {isAgentPost ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 fill-emerald-50" data-testid={`verified-tick-${post.post_id}`} />
              ) : isInstPost ? (
                // Institutions: single verified tick only. TrustBadge + GraduationCap
                // icons were removed — the per-category ribbon at the top already
                // communicates the trust signal (TEQSA / CRICOS / category).
                <CheckCircle2 className="h-3.5 w-3.5 text-[#2F80ED] fill-blue-50" data-testid={`inst-verified-${post.post_id}`} />
              ) : (
                <>
                  {post.is_verified && <CheckCircle2 className="h-3.5 w-3.5 text-[#2F80ED] fill-blue-50" />}
                  <TrustBadge tier={post.badge_tier} role={post.user_role} size="xs" />
                </>
              )}
            </div>
            {/* Agent meta — "MARA Agent · City" + rating row */}
            {isAgentPost && (post.agent_city || post.user_city) && (
              <div className="text-[10px] text-slate-500 font-medium" data-testid={`agent-meta-${post.post_id}`}>
                MARA Agent · {post.agent_city || post.user_city}
              </div>
            )}
            {/* Institution meta — "CRICOS: xxx · City" with graceful fallback */}
            {isInstPost && (() => {
              const cricos = post.inst_cricos;
              const city = post.inst_city || post.user_city;
              let metaText = '';
              if (cricos && city) metaText = `CRICOS: ${cricos} · ${city}`;
              else if (cricos) metaText = `CRICOS: ${cricos}`;
              else if (city) metaText = `Institution · ${city}`;
              else metaText = 'Verified Institution';
              return (
                <div className="text-[10px] text-slate-500 font-medium" data-testid={`inst-meta-${post.post_id}`}>
                  {metaText}
                </div>
              );
            })()}
            {isAgentPost && post.agent_rating > 0 && (
              <div className="text-[10px] text-amber-600 font-medium flex items-center gap-1">
                ⭐ {Number(post.agent_rating).toFixed(1)} · {post.agent_reviews || 0} reviews
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <span className="text-caption text-[#65676B]">{formatTime(post.created_at)}</span>
              {post.location && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{post.location}</span>}
            </div>
          </div>
        </div>
        {/* 3-dot menu */}
        <div className="relative">
          <button onClick={() => setMenuOpen(!menuOpen)} className="text-slate-300 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-all" data-testid={`post-menu-${post.post_id}`}>
            <MoreHorizontal className="h-5 w-5" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-8 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-20 min-w-[140px]" data-testid={`post-dropdown-${post.post_id}`}>
                {isOwner && (
                  <>
                    <button onClick={() => { setMenuOpen(false); setShowEdit(true); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50" data-testid={`edit-post-${post.post_id}`}>
                      <Pencil className="h-3.5 w-3.5" /> Edit Post
                    </button>
                    <button onClick={() => { setMenuOpen(false); onDelete(post.post_id); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50" data-testid={`delete-post-${post.post_id}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete Post
                    </button>
                  </>
                )}
                {!isOwner && (
                  <>
                    {isAllowed('social_follow') && <button onClick={handleFollow} disabled={followLoading} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#2F80ED] hover:bg-blue-50" data-testid={`follow-btn-${post.post_id}`}>
                      <Heart className="h-3.5 w-3.5" /> Follow {post.user_name?.split(' ')[0]}
                    </button>}
                    {isAllowed('social_dms') && <button onClick={handleSendDM} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50" data-testid={`dm-btn-${post.post_id}`}>
                      <MessageCircle className="h-3.5 w-3.5" /> Message
                    </button>}
                  </>
                )}
                {isAllowed('social_report_post') && <button onClick={() => { setMenuOpen(false); setShowReport(true); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-600 hover:bg-slate-50" data-testid={`report-post-${post.post_id}`}>
                  <Flag className="h-3.5 w-3.5" /> Report
                </button>}
                {!isOwner && isAllowed('social_block') && (
                  <button onClick={handleBlock} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-500 hover:bg-red-50" data-testid={`block-user-${post.post_id}`}>
                    <Shield className="h-3.5 w-3.5" /> Block {post.user_name?.split(' ')[0]}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        {/* Category badge — hidden on Lead Cards (top ribbon already shows it) */}
        {!isLead && post.category && CATEGORY_LABELS[post.category] && (
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold mb-2 ${CATEGORY_COLORS[post.category] || 'text-slate-500 bg-slate-50'}`} data-testid={`post-cat-${post.post_id}`}>
            {(() => { const CIcon = CATEGORY_ICONS[post.category]; return CIcon ? <CIcon className="h-3 w-3" /> : null; })()}
            {CATEGORY_LABELS[post.category]}
          </div>
        )}
        {post.visibility === 'followers' && (
          <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 mb-1 ml-2"><Lock className="h-3 w-3" /> Followers</span>
        )}
        {/* Consultation Offer / Special Offer price badge — renders only when
            both prices are supplied via post_metadata. Shared between agent
            Consultation Offer and institution Special Offer posts. */}
        {(isFreeConsult || isInstOffer) && hasOfferPrice && (
          <div className="flex items-center gap-2 mb-3 p-2.5 rounded-xl bg-amber-50 border border-amber-200" data-testid={`offer-price-${post.post_id}`}>
            <span className="text-sm text-slate-400 line-through font-medium">${post.post_metadata.original_fee}</span>
            <ArrowRight className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-lg font-black" style={{ color: '#0F2240' }}>${post.post_metadata.offer_price}</span>
          </div>
        )}
        {(() => {
          // Truncate only announcement posts past 280 chars. Other content
          // renders in full as before.
          const isLongAnnouncement = isInstPost && post.category === 'announcement' && (post.content?.length || 0) > 280;
          const shown = isLongAnnouncement && !expandedAnnouncement
            ? post.content.slice(0, 280).trimEnd() + '…'
            : post.content;
          return (
            <>
              <p className="text-body text-[#0A2540] leading-relaxed whitespace-pre-line" data-testid={`post-content-${post.post_id}`}>{shown}</p>
              {post.edited && <span className="text-[10px] text-slate-400 ml-1">(edited)</span>}
            </>
          );
        })()}
      </div>

      {/* Hashtags */}
      {post.hashtags?.length > 0 && (
        <div className="flex gap-1.5 px-4 pb-2 flex-wrap">
          {post.hashtags.map((tag) => <span key={tag} className="text-xs text-[#2F80ED] font-medium cursor-pointer hover:underline">#{tag}</span>)}
        </div>
      )}

      {/* Tags */}
      {post.tags?.length > 0 && (
        <div className="flex gap-1.5 px-4 pb-3 flex-wrap">
          {post.tags.map((tag) => <span key={tag} className="text-sm text-[#2F80ED] font-medium">#{tag}</span>)}
        </div>
      )}

      {/* Images / Carousel */}
      {images.length > 0 && (
        <div className="relative w-full overflow-hidden">
          {images.length === 1 ? (
            <div className="w-full aspect-[4/3] cursor-pointer" onClick={() => setLightbox({ images, index: 0 })}>
              <img src={images[0]} alt="" className="w-full h-full object-cover" loading="lazy" data-testid={`post-image-${post.post_id}`} />
            </div>
          ) : (
            <div className="relative">
              <div className="w-full aspect-[4/3] cursor-pointer" onClick={() => setLightbox({ images, index: carouselIdx })}>
                <img src={images[carouselIdx]} alt="" className="w-full h-full object-cover transition-opacity" loading="lazy" data-testid={`post-image-${post.post_id}`} />
              </div>
              {/* Carousel arrows */}
              <button onClick={() => setCarouselIdx(i => (i - 1 + images.length) % images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => setCarouselIdx(i => (i + 1) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50"><ChevronRight className="h-4 w-4" /></button>
              {/* Dots */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {images.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === carouselIdx ? 'bg-white scale-125' : 'bg-white/50'}`} />)}
              </div>
              <span className="absolute top-2 right-2 text-[10px] bg-black/40 text-white px-2 py-0.5 rounded-full font-medium">{carouselIdx + 1}/{images.length}</span>
            </div>
          )}
        </div>
      )}

      {/* Repost Embed */}
      {post.is_repost && post.repost_of && (
        <div className="mx-4 mb-3 p-3 rounded-xl border border-slate-200 bg-slate-50/50" data-testid={`repost-embed-${post.post_id}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center overflow-hidden cursor-pointer"
              onClick={() => post.repost_of.user_id && navigate(`/user/${post.repost_of.user_id}`)}>
              {post.repost_of.user_avatar ? <img src={resolveImageUrl(post.repost_of.user_avatar)} alt="" className="w-full h-full object-cover" /> :
                <span className="text-caption font-bold text-[#2F80ED]">{post.repost_of.user_name?.[0]}</span>}
            </div>
            <span className="text-sm font-semibold text-[#0A2540] cursor-pointer hover:text-[#2F80ED] transition-colors"
              onClick={() => post.repost_of.user_id && navigate(`/user/${post.repost_of.user_id}`)}>{post.repost_of.user_name}</span>
            <span className="text-caption text-[#65676B]">{formatTime(post.repost_of.created_at)}</span>
          </div>
          <p className="text-comment text-[#65676B] leading-relaxed line-clamp-3">{post.repost_of.content}</p>
          {post.repost_of.images?.length > 0 && (
            <img src={resolveImageUrl(post.repost_of.images[0])} alt="" className="w-full h-32 object-cover rounded-lg mt-2" />
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-5">
          {isAllowed('social_likes') && <button onClick={() => gate('Join the conversation', () => onLike(post.post_id))} className={`flex items-center gap-1.5 transition-all active:scale-90 ${liked ? 'text-red-500' : 'text-slate-400 hover:text-slate-600'}`} data-testid={`like-btn-${post.post_id}`}>
            <Heart className={`h-5 w-5 ${liked ? 'fill-current heart-explode' : ''}`} />
            <span className="text-xs font-mono">{post.like_count}</span>
          </button>}
          {isAllowed('social_comments') && <button onClick={() => gate('Join the conversation', () => setCommentsOpen(!commentsOpen))} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 transition-all active:scale-90" data-testid={`comment-btn-${post.post_id}`}>
            <MessageCircle className="h-5 w-5" />
            <span className="text-xs font-mono">{post.comment_count}</span>
          </button>}
          <ShareMenu url={postUrl} title={`${post.user_name}: ${post.content?.slice(0, 60)}...`} postId={post.post_id} />
          {isAllowed('social_repost') && <button onClick={() => gate('Join the conversation', () => onRepost(post.post_id))} className="flex items-center gap-1.5 text-slate-400 hover:text-emerald-500 transition-all active:scale-90" data-testid={`repost-btn-${post.post_id}`}>
            <Repeat2 className="h-5 w-5" />
            <span className="text-xs font-mono">{post.repost_count || 0}</span>
          </button>}
        </div>
        {isAllowed('social_bookmarks') && <button onClick={() => gate('Sign up to save this', () => onBookmark(post.post_id))} className={`transition-all active:scale-90 ${bookmarked ? 'text-[#2F80ED]' : 'text-slate-400 hover:text-slate-600'}`} data-testid={`save-btn-${post.post_id}`}>
          <Bookmark className={`h-5 w-5 ${bookmarked ? 'fill-current' : ''}`} />
        </button>}
      </div>

      {/* Guest signup gate — opens when an unauthenticated user attempts
          any action above. Renders QuickOTPModal in guest mode with the
          action-specific title (e.g. "Join the conversation"). */}
      {sheetOpen && (
        <GuestGateModal
          title={sheetTitle}
          returnUrl={returnUrl}
          onClose={() => setSheetOpen(false)}
        />
      )}

      {/* Connect button — only on Lead Cards, visible to agents + institutions */}
      {canConnect && (
        <div className="px-4 pb-3">
          <button
            onClick={() => handleConnect(post)}
            className="w-full py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-purple-200 active:scale-[0.98] transition-all"
            data-testid={`connect-btn-${post.post_id}`}
          >
            🤝 Connect
          </button>
        </div>
      )}

      {/* Institution CTA — per-category. Button colour matches the ribbon to
          give each card a coherent visual identity. Destinations vary per
          category (claim modal, course page, expand-in-place, profile). */}
      {isInstPost && !hideProfileCTA && (() => {
        const INST_CTAS = {
          scholarship:      { text: 'Apply for Scholarship →', bg: '#F5A623', color: '#0F2240' },
          special_offer:    { text: 'Claim This Offer →',      bg: '#7C3AED', color: '#FFFFFF' },
          course_spotlight: { text: 'View Course →',           bg: '#2563EB', color: '#FFFFFF' },
          announcement:     { text: 'Read More →',             bg: '#374151', color: '#FFFFFF' },
        };
        const cta = INST_CTAS[post.category];
        if (!cta) return null;

        const slug = post.post_metadata?.course_slug || post.post_metadata?.course_id;
        const hasUniRoute = !!post.university_id;
        const isAnnouncement = post.category === 'announcement';
        const isLongAnnouncement = isAnnouncement && (post.content?.length || 0) > 280;

        // Hide Read More entirely for short announcements (full text is
        // already visible). For long ones, swap the label after expand.
        if (isAnnouncement && !isLongAnnouncement) return null;
        const ctaText = isAnnouncement && expandedAnnouncement ? 'Show Less ↑' : cta.text;

        const handleClick = () => {
          if (post.category === 'special_offer') {
            setShowClaimOffer(true);
            return;
          }
          if (post.category === 'course_spotlight' && slug) {
            navigate(`/courses/${slug}`);
            return;
          }
          if (isAnnouncement) {
            setExpandedAnnouncement(v => !v);
            return;
          }
          // scholarship / fallback → institution profile
          if (hasUniRoute) navigate(`/university/${post.university_id}`);
        };

        // Disable the CTA only when there's literally nowhere to go (no
        // university_id AND no course slug AND not the in-place expand).
        const disabled = !isAnnouncement && post.category !== 'special_offer' && !hasUniRoute && !slug;
        if (disabled) return null;
        // Admin Hidden gate — if Claim Offer is hidden in the Credit Engine
        // we suppress just that variant (other inst CTAs still render).
        if (post.category === 'special_offer' && claimHidden) return null;

        return (
          <div className="px-4 pb-3">
            <button
              onClick={handleClick}
              className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-all"
              style={{ backgroundColor: cta.bg, color: cta.color }}
              data-testid={`inst-cta-${post.post_id}`}
            >
              {ctaText}
            </button>
          </div>
        );
      })()}

      {/* Claim Offer modal — mounts only when student taps the CTA */}
      {showClaimOffer && (
        <ClaimOfferModal
          post={post}
          onClose={() => setShowClaimOffer(false)}
        />
      )}

      {/* Book Consultation modal — opens for student-side bookings of agent
          free_consultation posts. Free — uses /agents/:id/request-consult. */}
      {showBookConsult && (
        <BookConsultationModal
          agent={{ user_id: post.user_id, name: post.user_name }}
          onClose={() => setShowBookConsult(false)}
          onSuccess={() => setShowBookConsult(false)}
        />
      )}

      {/* Agent CTA — Book Free Consultation (amber filled) or View Agent Profile (teal outlined). Book Consultation respects the Credit Engine "Hidden" toggle: when admin turns it off, the button disappears and the backend refuses the action. */}
      {isAgentPost && !(hideProfileCTA && !isFreeConsult) && !(isFreeConsult && consultHidden) && (
        <div className="px-4 pb-3">
          {isFreeConsult ? (
            <button
              onClick={() => handleBookConsultation(post)}
              className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-all"
              style={{ backgroundColor: '#F5A623', color: '#0F2240' }}
              data-testid={`book-consult-${post.post_id}`}
            >
              🤝 Book Consultation →
            </button>
          ) : (
            <button
              onClick={handleProfileClick}
              className="w-full py-2.5 rounded-xl border-2 text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
              style={{ borderColor: '#1A7A6E', color: '#1A7A6E', backgroundColor: 'rgba(26,122,110,0.05)' }}
              data-testid={`view-agent-${post.post_id}`}
            >
              View Agent Profile →
            </button>
          )}
        </div>
      )}

      {/* Comments */}
      {isAllowed('social_comments') && <CommentSection postId={post.post_id} commentCount={post.comment_count} isOpen={commentsOpen} onToggle={setCommentsOpen} postOwnerId={post.user_id} />}

      {/* Modals */}
      {lightbox && <ImageLightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
      {showReport && <ReportModal postId={post.post_id} onClose={() => setShowReport(false)} />}
      {showEdit && <EditModal post={post} onClose={() => setShowEdit(false)} onSave={(content) => onEditSave(post.post_id, content)} />}
    </article>
  );
}

function formatTime(isoStr) {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
